import React, { useState, useRef, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import toast from "react-hot-toast";
import { chatAPI, exportAPI } from "../services/api";

const MODES = [{ id:"hybrid",label:"Hybrid" },{ id:"vector",label:"Vector" },{ id:"keyword",label:"Keyword" }];

function ConfidenceBar({ value }) {
  const pct = Math.round(value * 100);
  const color = pct >= 70 ? "#14b88e" : pct >= 40 ? "#f59e0b" : "#ef4444";
  return (
    <div style={{ display:"flex", alignItems:"center", gap:"6px" }}>
      <div style={{ width:"80px", height:"3px", background:"#1e2a50", borderRadius:"999px", overflow:"hidden" }}>
        <div style={{ width:`${pct}%`, height:"100%", background:color, borderRadius:"999px", transition:"width 0.6s" }} />
      </div>
      <span style={{ fontSize:"11px", color:"#455a9e", fontFamily:"monospace" }}>{pct}%</span>
    </div>
  );
}

function SourceCard({ source, index }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ border:"1px solid #2a3a6e", borderRadius:"8px", overflow:"hidden", marginBottom:"4px" }}>
      <button onClick={() => setOpen(!open)} style={{ width:"100%", display:"flex", alignItems:"center", gap:"8px", padding:"7px 10px", background:"#1e2a50", border:"none", cursor:"pointer" }}>
        <span style={{ width:"18px", height:"18px", borderRadius:"5px", background:"rgba(20,184,142,0.1)", border:"1px solid rgba(20,184,142,0.3)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"10px", fontWeight:700, color:"#14b88e", flexShrink:0 }}>{index}</span>
        <span style={{ flex:1, fontSize:"12px", color:"#8fa1cd", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", textAlign:"left" }}>{source.doc_name}</span>
        <span style={{ fontSize:"11px", color:"#455a9e", fontFamily:"monospace" }}>{Math.round(source.score*1000)/10}%</span>
        <span style={{ color:"#455a9e", fontSize:"11px" }}>{open?"▴":"▾"}</span>
      </button>
      {open && (
        <div style={{ padding:"8px 10px", background:"#141c36", borderTop:"1px solid #2a3a6e" }}>
          <p style={{ fontSize:"12px", color:"#8fa1cd", lineHeight:"1.6", fontFamily:"monospace" }}>{source.text}</p>
        </div>
      )}
    </div>
  );
}

function BrainIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#14b88e" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9.5 2A2.5 2.5 0 0 1 12 4.5v15a2.5 2.5 0 0 1-4.96-.46 2.5 2.5 0 0 1-2.96-3.08 3 3 0 0 1-.34-5.58 2.5 2.5 0 0 1 1.32-4.24 2.5 2.5 0 0 1 1.98-3A2.5 2.5 0 0 1 9.5 2Z"/>
      <path d="M14.5 2A2.5 2.5 0 0 0 12 4.5v15a2.5 2.5 0 0 0 4.96-.46 2.5 2.5 0 0 0 2.96-3.08 3 3 0 0 0 .34-5.58 2.5 2.5 0 0 0-1.32-4.24 2.5 2.5 0 0 0-1.98-3A2.5 2.5 0 0 0 14.5 2Z"/>
    </svg>
  );
}

function Avatar() {
  return (
    <div style={{ width:"32px", height:"32px", borderRadius:"10px", flexShrink:0, background:"rgba(20,184,142,0.1)", border:"1px solid rgba(20,184,142,0.25)", display:"flex", alignItems:"center", justifyContent:"center" }}>
      <BrainIcon />
    </div>
  );
}

function Message({ msg }) {
  const [showSources, setShowSources] = useState(false);
  const isUser = msg.role === "user";

  if (isUser) {
    return (
      <div style={{ display:"flex", justifyContent:"flex-end", gap:"10px", marginBottom:"24px", alignItems:"flex-start" }}>
        <div style={{ maxWidth:"65%", background:"#1e2a50", color:"#e6edf3", borderRadius:"18px 18px 4px 18px", padding:"12px 16px", fontSize:"14px", lineHeight:"1.6", border:"1px solid #2a3a6e" }}>
          {msg.content}
        </div>
        <div style={{ width:"32px", height:"32px", borderRadius:"50%", background:"#1e2a50", border:"1px solid #2a3a6e", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"12px", fontWeight:700, color:"#8fa1cd", flexShrink:0 }}>U</div>
      </div>
    );
  }

  return (
    <div style={{ display:"flex", gap:"12px", marginBottom:"28px" }}>
      <div style={{ marginTop:"2px" }}><Avatar /></div>
      <div style={{ flex:1, maxWidth:"780px" }}>
        <div style={{ color:"#e2e8f0", lineHeight:"1.7", fontSize:"14px" }}>
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={{
            p: ({children}) => <p style={{ marginBottom:"10px" }}>{children}</p>,
            h1: ({children}) => <h1 style={{ fontSize:"20px", fontWeight:700, color:"white", margin:"14px 0 6px" }}>{children}</h1>,
            h2: ({children}) => <h2 style={{ fontSize:"17px", fontWeight:700, color:"white", margin:"14px 0 6px" }}>{children}</h2>,
            h3: ({children}) => <h3 style={{ fontSize:"15px", fontWeight:700, color:"white", margin:"14px 0 6px" }}>{children}</h3>,
            ul: ({children}) => <ul style={{ paddingLeft:"20px", marginBottom:"10px" }}>{children}</ul>,
            ol: ({children}) => <ol style={{ paddingLeft:"20px", marginBottom:"10px" }}>{children}</ol>,
            li: ({children}) => <li style={{ marginBottom:"4px" }}>{children}</li>,
            strong: ({children}) => <strong style={{ fontWeight:600, color:"white" }}>{children}</strong>,
            code: ({inline, children}) => inline
              ? <code style={{ background:"#1e2a50", border:"1px solid #2a3a6e", padding:"1px 5px", borderRadius:"4px", fontFamily:"monospace", fontSize:"12px", color:"#14b88e" }}>{children}</code>
              : <pre style={{ background:"#1e2a50", border:"1px solid #2a3a6e", borderRadius:"8px", padding:"12px", marginBottom:"10px", overflowX:"auto" }}><code style={{ fontFamily:"monospace", fontSize:"12px", color:"#e2e8f0" }}>{children}</code></pre>,
            table: ({children}) => <table style={{ width:"100%", borderCollapse:"collapse", marginBottom:"12px", fontSize:"13px" }}>{children}</table>,
            th: ({children}) => <th style={{ background:"#1e2a50", border:"1px solid #2a3a6e", padding:"8px 12px", textAlign:"left", fontSize:"11px", fontWeight:600, textTransform:"uppercase", color:"#8fa1cd" }}>{children}</th>,
            td: ({children}) => <td style={{ border:"1px solid #2a3a6e", padding:"8px 12px" }}>{children}</td>,
            blockquote: ({children}) => <blockquote style={{ borderLeft:"3px solid rgba(20,184,142,0.3)", paddingLeft:"12px", color:"#8fa1cd", fontStyle:"italic" }}>{children}</blockquote>,
            a: ({href, children}) => <a href={href} style={{ color:"#14b88e", textDecoration:"underline" }}>{children}</a>,
          }}>
            {msg.content}
          </ReactMarkdown>
        </div>

        {msg.confidence !== undefined && (
          <div style={{ display:"flex", alignItems:"center", gap:"10px", marginTop:"10px", flexWrap:"wrap" }}>
            <ConfidenceBar value={msg.confidence} />
            {msg.rewritten_query && msg.rewritten_query !== msg.originalQuery && (
              <span style={{ fontSize:"11px", color:"#14b88e" }}>↻ Query rewritten</span>
            )}
            {msg.contradiction_warning && (
              <span style={{ display:"inline-flex", padding:"3px 10px", borderRadius:"999px", fontSize:"10px", fontWeight:500, border:"1px solid rgba(245,158,11,0.3)", background:"rgba(245,158,11,0.1)", color:"#f59e0b" }}>⚠ Conflicting info</span>
            )}
            {msg.crag_flagged && (
              <span style={{ display:"inline-flex", padding:"3px 10px", borderRadius:"999px", fontSize:"10px", fontWeight:500, border:"1px solid rgba(239,68,68,0.3)", background:"rgba(239,68,68,0.1)", color:"#ef4444" }}>⚠ May hallucinate</span>
            )}
            {msg.sources?.length > 0 && (
              <button onClick={() => setShowSources(!showSources)}
                style={{ fontSize:"11px", color:"#455a9e", background:"none", border:"none", cursor:"pointer", padding:0, marginLeft:"auto", fontFamily:"inherit" }}>
                {showSources?"Hide":"Show"} {msg.sources.filter((s,i,a)=>a.findIndex(x=>x.doc_name===s.doc_name&&x.chunk_index===s.chunk_index)===i).length} sources
              </button>
            )}
          </div>
        )}
        {showSources && msg.sources && (
          <div style={{ marginTop:"8px" }}>
            {msg.sources.filter((s,i,a)=>a.findIndex(x=>x.doc_name===s.doc_name&&x.chunk_index===s.chunk_index)===i).map((src,i)=>(
              <SourceCard key={src.chunk_id} source={src} index={i+1} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function TypingIndicator() {
  return (
    <div style={{ display:"flex", gap:"12px", marginBottom:"28px" }}>
      <Avatar />
      <div style={{ paddingTop:"8px", display:"flex", gap:"4px", alignItems:"center" }}>
        {[0,1,2].map(i=>(
          <span key={i} style={{ width:"5px", height:"5px", borderRadius:"50%", background:"#14b88e", display:"inline-block", animation:"dotB 1.2s infinite", animationDelay:`${i*0.2}s` }} />
        ))}
      </div>
      <style>{`@keyframes dotB{0%,80%,100%{transform:scale(0.6);opacity:0.4}40%{transform:scale(1);opacity:1}}`}</style>
    </div>
  );
}

export default function ChatPage({ documents, chatState, setChatState }) {
  const [loading, setLoading] = useState(false);
  const [chatDropdownOpen, setChatDropdownOpen] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  const messages = chatState.messages;
  const input = chatState.input;
  const searchMode = chatState.searchMode;
  const chats = chatState.chats || [];
  const activeChatId = chatState.activeChatId;

  const setMessages = v => setChatState(p=>({...p,messages:typeof v==="function"?v(p.messages):v}));
  const setInput = v => setChatState(p=>({...p,input:v}));
  const setSearchMode = v => setChatState(p=>({...p,searchMode:v}));

  useEffect(() => { messagesEndRef.current?.scrollIntoView({behavior:"smooth"}); }, [messages, loading]);

  useEffect(() => {
    const h = e => { if(dropdownRef.current&&!dropdownRef.current.contains(e.target)) setChatDropdownOpen(false); };
    document.addEventListener("mousedown",h);
    return () => document.removeEventListener("mousedown",h);
  }, []);

  const saveCurrentChat = (msgs) => {
    if (!msgs.length) return;
    const title = msgs[0]?.content?.slice(0,45)+(msgs[0]?.content?.length>45?"...":"") || "New chat";
    setChatState(prev => {
      const id = prev.activeChatId || Date.now().toString();
      const chat = { id, title, messages: msgs, createdAt: new Date().toISOString() };
      const idx = prev.chats.findIndex(c=>c.id===id);
      const newChats = idx>=0 ? prev.chats.map(c=>c.id===id?chat:c) : [chat,...prev.chats];
      return { ...prev, chats: newChats, activeChatId: id };
    });
  };

  const startNewChat = () => { saveCurrentChat(messages); setChatState(p=>({...p,messages:[],input:"",activeChatId:null})); setChatDropdownOpen(false); };
  const loadChat = chat => { saveCurrentChat(messages); setChatState(p=>({...p,messages:chat.messages,activeChatId:chat.id})); setChatDropdownOpen(false); };
  const deleteChat = (id,e) => { e.stopPropagation(); setChatState(p=>({...p,chats:p.chats.filter(c=>c.id!==id),activeChatId:p.activeChatId===id?null:p.activeChatId})); };

  const handleSend = async () => {
    if (!input.trim()||loading) return;
    const userMsg = {role:"user",content:input};
    const history = messages.map(m=>({role:m.role,content:m.content}));
    const newMessages = [...messages,userMsg];
    setMessages(newMessages); setInput(""); setLoading(true);
    try {
      const res = await chatAPI.send({query:input,search_mode:searchMode,conversation_history:history.slice(-6)});
      const d = res.data;
      const final = [...newMessages,{role:"assistant",content:d.answer,sources:d.sources,confidence:d.confidence,rewritten_query:d.rewritten_query,originalQuery:input,contradiction_warning:d.contradiction_warning,crag_flagged:d.crag_flagged}];
      setMessages(final);
      saveCurrentChat(final);
    } catch { toast.error("Failed to get response"); setMessages(newMessages.slice(0,-1)); }
    finally { setLoading(false); }
  };

  const handleExport = async () => {
    try {
      const res = await exportAPI.exportPDF(messages,"Chat Export");
      const url=URL.createObjectURL(new Blob([res.data]));
      const a=document.createElement("a"); a.href=url; a.download="chat.pdf"; a.click();
      URL.revokeObjectURL(url); toast.success("Exported!");
    } catch { toast.error("Export failed"); }
  };

  const currentTitle = activeChatId ? chats.find(c=>c.id===activeChatId)?.title||"Chat" : messages.length>0 ? messages[0]?.content?.slice(0,28)+"..." : "New chat";

  return (
    <div style={{ display:"flex", flexDirection:"column", height:"100%", background:"#0d1117" }}>
      {/* Mode bar */}
      <div style={{ display:"flex", alignItems:"center", gap:"8px", padding:"10px 20px", borderBottom:"1px solid #21293d", flexShrink:0 }}>
        <div style={{ display:"flex", gap:"4px" }}>
          {MODES.map(m=>(
            <button key={m.id} onClick={()=>setSearchMode(m.id)}
              style={{ padding:"5px 16px", borderRadius:"8px", fontSize:"13px", fontWeight:500, cursor:"pointer", border:`1px solid ${searchMode===m.id?"#14b88e":"#21293d"}`, background:searchMode===m.id?"#14b88e":"transparent", color:searchMode===m.id?"white":"#7d8590", transition:"all 0.15s", fontFamily:"inherit" }}>
              {m.label}
            </button>
          ))}
        </div>
        <div style={{ width:"1px", height:"18px", background:"#21293d", margin:"0 4px" }} />
        <span style={{ fontSize:"12px", color:"#484f58", display:"flex", alignItems:"center", gap:"5px" }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none"><rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/><path d="M3 9h18M9 21V9" stroke="currentColor" strokeWidth="2"/></svg>
          All {documents.length} docs
        </span>
        <div style={{ flex:1 }} />

        {/* Chat dropdown */}
        <div style={{ position:"relative" }} ref={dropdownRef}>
          <button onClick={()=>setChatDropdownOpen(o=>!o)}
            style={{ display:"flex", alignItems:"center", gap:"6px", padding:"5px 12px", borderRadius:"8px", background:"transparent", border:"1px solid #21293d", color:"#7d8590", fontSize:"12px", cursor:"pointer", fontFamily:"inherit" }}>
            <span>💬</span>
            <span style={{ maxWidth:"120px", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{currentTitle}</span>
            <span style={{ fontSize:"10px" }}>▾</span>
          </button>
          {chatDropdownOpen && (
            <div style={{ position:"absolute", top:"calc(100% + 6px)", right:0, width:"270px", background:"#161b27", border:"1px solid #21293d", borderRadius:"10px", boxShadow:"0 8px 28px rgba(0,0,0,0.6)", zIndex:200, overflow:"hidden" }}>
              <button onClick={startNewChat}
                style={{ width:"100%", padding:"10px 14px", display:"flex", alignItems:"center", gap:"6px", background:"none", border:"none", borderBottom:"1px solid #21293d", cursor:"pointer", fontSize:"13px", fontWeight:600, color:"#14b88e", textAlign:"left", fontFamily:"inherit" }}
                onMouseEnter={e=>e.currentTarget.style.background="rgba(20,184,142,0.08)"}
                onMouseLeave={e=>e.currentTarget.style.background="none"}>
                ＋ New Chat
              </button>
              <div style={{ maxHeight:"280px", overflowY:"auto" }}>
                {chats.length===0
                  ? <div style={{ padding:"16px", textAlign:"center", fontSize:"12px", color:"#484f58" }}>No saved chats yet</div>
                  : chats.map(chat=>(
                    <div key={chat.id} onClick={()=>loadChat(chat)}
                      style={{ display:"flex", alignItems:"center", gap:"8px", padding:"9px 14px", cursor:"pointer", background:chat.id===activeChatId?"rgba(20,184,142,0.08)":"none", borderBottom:"1px solid #21293d" }}
                      onMouseEnter={e=>{if(chat.id!==activeChatId)e.currentTarget.style.background="#1c2333";}}
                      onMouseLeave={e=>{if(chat.id!==activeChatId)e.currentTarget.style.background="none";}}>
                      <span>💬</span>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:"12px", fontWeight:500, color:"#e6edf3", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{chat.title}</div>
                        <div style={{ fontSize:"10px", color:"#484f58" }}>{chat.messages.length} messages</div>
                      </div>
                      <button onClick={e=>deleteChat(chat.id,e)}
                        style={{ background:"none", border:"none", cursor:"pointer", color:"#484f58", fontSize:"15px", borderRadius:"4px", padding:"2px 4px" }}
                        onMouseEnter={e=>{e.currentTarget.style.color="#ef4444";e.currentTarget.style.background="rgba(239,68,68,0.1)";}}
                        onMouseLeave={e=>{e.currentTarget.style.color="#484f58";e.currentTarget.style.background="none";}}>×</button>
                    </div>
                  ))
                }
              </div>
            </div>
          )}
        </div>

        <button onClick={startNewChat} style={{ padding:"5px 12px", borderRadius:"8px", background:"transparent", border:"1px solid #21293d", color:"#7d8590", fontSize:"12px", cursor:"pointer", fontFamily:"inherit" }}>+ New Chat</button>
        {messages.length>0 && <button onClick={handleExport} style={{ padding:"5px 12px", borderRadius:"8px", background:"transparent", border:"1px solid #21293d", color:"#7d8590", fontSize:"12px", cursor:"pointer", fontFamily:"inherit" }}>↓ Export PDF</button>}
      </div>

      {/* Messages */}
      <div style={{ flex:1, overflowY:"auto", padding:"32px 20px 20px" }}>
        <div style={{ maxWidth:"800px", margin:"0 auto" }}>
          {messages.length===0 && (
            <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", minHeight:"60vh", textAlign:"center" }}>
              <div style={{ width:"64px", height:"64px", borderRadius:"18px", background:"rgba(20,184,142,0.12)", border:"1px solid rgba(20,184,142,0.25)", display:"flex", alignItems:"center", justifyContent:"center", marginBottom:"20px" }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#14b88e" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9.5 2A2.5 2.5 0 0 1 12 4.5v15a2.5 2.5 0 0 1-4.96-.46 2.5 2.5 0 0 1-2.96-3.08 3 3 0 0 1-.34-5.58 2.5 2.5 0 0 1 1.32-4.24 2.5 2.5 0 0 1 1.98-3A2.5 2.5 0 0 1 9.5 2Z"/>
                  <path d="M14.5 2A2.5 2.5 0 0 0 12 4.5v15a2.5 2.5 0 0 0 4.96-.46 2.5 2.5 0 0 0 2.96-3.08 3 3 0 0 0 .34-5.58 2.5 2.5 0 0 0-1.32-4.24 2.5 2.5 0 0 0-1.98-3A2.5 2.5 0 0 0 14.5 2Z"/>
                </svg>
              </div>
              <h2 style={{ fontSize:"26px", fontWeight:700, color:"#e6edf3", marginBottom:"10px" }}>Adaptive Retrieval System</h2>
              <p style={{ fontSize:"14px", color:"#7d8590", lineHeight:"1.7", maxWidth:"460px", marginBottom:"28px" }}>
                Upload documents in the sidebar, then ask questions. The system uses hybrid search, CrossEncoder reranking, query rewriting, and conversation memory.
              </p>
              <div style={{ display:"flex", flexWrap:"wrap", gap:"8px", justifyContent:"center" }}>
                {["Hybrid Search","Reranking","Query Rewriting","Contradiction Detection","CRAG","Semantic Chunking"].map(t=>(
                  <span key={t} style={{ padding:"3px 10px", borderRadius:"999px", fontSize:"11px", fontWeight:500, border:"1px solid #21293d", color:"#7d8590" }}>{t}</span>
                ))}
              </div>
            </div>
          )}
          {messages.map((m,i)=><Message key={i} msg={m}/>)}
          {loading && <TypingIndicator />}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input */}
      <div style={{ padding:"14px 20px 18px", borderTop:"1px solid #21293d", background:"#0d1117", flexShrink:0 }}>
        <div style={{ maxWidth:"800px", margin:"0 auto", display:"flex", gap:"10px", alignItems:"flex-end" }}>
          <textarea ref={inputRef} value={input} onChange={e=>setInput(e.target.value)}
            onKeyDown={e=>{ if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();handleSend();}}}
            placeholder="Ask a question about your documents..."
            rows={1}
            style={{ flex:1, background:"#161b27", border:"1px solid #21293d", borderRadius:"12px", padding:"12px 16px", fontSize:"14px", color:"#e6edf3", outline:"none", resize:"none", minHeight:"48px", maxHeight:"120px", lineHeight:"1.5", fontFamily:"inherit" }}
            onFocus={e=>e.target.style.borderColor="#14b88e"}
            onBlur={e=>e.target.style.borderColor="#21293d"}
          />
          <button onClick={handleSend} disabled={loading||!input.trim()}
            style={{ width:"42px", height:"42px", borderRadius:"10px", background:"#14b88e", border:"none", color:"white", fontSize:"18px", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, opacity:(loading||!input.trim())?0.4:1 }}>
            →
          </button>
        </div>
        <div style={{ maxWidth:"800px", margin:"6px auto 0", fontSize:"11px", color:"#484f58" }}>Enter to send · Shift+Enter for new line</div>
      </div>
    </div>
  );
}